import { Controller } from "@hotwired/stimulus"
import SlimSelect from "slim-select"
import { slimSelectSettings } from "../helpers/slim_select_helpers"

export default class extends Controller {
  static targets = ["select", "name"]
  static values = {
    createUrl: { type: String, default: "/fleets/quick_create" },
    canCreate: { type: Boolean, default: false },
    // The block keeps an unknown device on the row (`text:<name>`) instead of creating a
    // Fleet — see CustomReports::FleetValue.
    freeText: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: "Suchen..." },
    searchOrCreatePlaceholder: { type: String, default: "Suchen oder neu erstellen" },
    addableText: { type: String, default: '"{value}" hinzufügen (Enter drücken)' },
  }

  connect() {
    if (!this.hasSelectTarget) return

    const config = {
      select: this.selectTarget,
      settings: slimSelectSettings({
        allowDeselect: true,
        showSearch: true,
        searchPlaceholder: this.canCreateValue
          ? this.searchOrCreatePlaceholderValue
          : this.searchPlaceholderValue,
        placeholderText: this.selectTarget.dataset.placeholder,
      }),
      events: {
        searchFilter: (option, search) => {
          if (search.length === 0) return true
          const searchTerms = search.toLowerCase().split(" ")
          const optionText = option.text.toLowerCase()
          return searchTerms.every((term) => optionText.includes(term))
        },
        afterChange: (newVal) => {
          this.handleSelectionChange(newVal)
        },
      },
    }

    if (this.canCreateValue) {
      config.settings.addableText = this.addableTextValue
      config.events.addable = (value) => {
        return this.freeTextValue ? this.freeTextOption(value) : this.createFleet(value)
      }
    }

    this.slimSelect = new SlimSelect(config)
  }

  disconnect() {
    if (this.slimSelect) {
      this.slimSelect.destroy()
    }
  }

  handleSelectionChange(newVal) {
    if (!this.hasNameTarget) return

    const selectedValue = newVal && newVal.length > 0 ? newVal[0].value : ""
    const selectedText = newVal && newVal.length > 0 ? newVal[0].text : ""

    this.nameTarget.value = selectedValue ? selectedText : ""
  }

  freeTextOption(name) {
    const trimmedName = name.trim()
    if (trimmedName.length === 0) return false

    return { text: trimmedName, value: `text:${trimmedName}` }
  }

  async createFleet(name) {
    const trimmedName = name.trim()
    if (trimmedName.length === 0) return false

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ name: trimmedName }),
      })

      if (!response.ok) return false

      const data = await response.json()
      // display_name is the tenant-configured label; name is the raw column we posted.
      return { text: data.display_name || data.name, value: String(data.id) }
    } catch (error) {
      console.error("Fleet creation failed:", error)
      return false
    }
  }
}
